import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  HStack,
  Icon,
  IconButton,
  Input,
  InputGroup,
  InputRightElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Text,
  Textarea,
} from "@chakra-ui/react";
import {
  type KeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useTranslation } from "react-i18next";
import { FiCheck, FiX } from "react-icons/fi";
import {
  FoaPart,
  type RejectionGroundDto,
  type ViewDocumentAsOwnerDto,
} from "../../autogen/bff-api";
import { DropTarget } from "../documents/UppyUploader/DropTarget";
import {
  FileRow,
  isIncompleteFileUpload,
  type FileUpload,
  UploadCompletedStatus,
} from "../documents/UppyUploader/FileRow";
import type { DocumentUploadContext } from "../documents/UppyUploader/useDocumentsUploader";
import { useUppyUploader } from "../documents/UppyUploader/useUppyUploader";
import { TipTap } from "../input/TipTap/TipTap";
import { getTiptapJsonFromMarkdown } from "../input/TipTap/markdownUtils";
import {
  type RejectionGroundGroup,
  citesRegulation,
  getApplicableRejectionGroundGroups,
} from "./rejectionGrounds";
import {
  REJECTION_COMPLAINT_INFO,
  buildPrivateRejectionMessageMarkdown,
  buildSupplierRejectionMessageMarkdown,
  getGroundMessageMarkdown,
  supplierRejectionLead,
  type PrivateRejectionMessageContext,
} from "./rejectionMessage";

export interface RejectionSubmission {
  grounds: RejectionGroundDto[];
  reason: string;
  message: string;
  attachmentIds: string[];
}

/**
 * The uploader differs per rejected entity (a tilbud vs a forespørsel om å delta) — each has its own
 * upload context and its own documents query — so the caller supplies the wiring.
 */
export interface RejectionAttachmentsSource {
  entityType: DocumentUploadContext;
  entityId: string;
  eventId: string;
  documents: ViewDocumentAsOwnerDto[];
  refetch: () => void;
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  supplierName: string;
  organizationNumber?: string | null;
  foaPart?: FoaPart | null;
  includeTenderGrounds?: boolean;
  initialGroundKeys?: string[];
  attachments: RejectionAttachmentsSource;
  onSubmit: (submission: RejectionSubmission) => Promise<void>;
  isSubmitting: boolean;
  submitLabel?: string;
  // Private organizations are outside LOA/FOA, so no statutory grounds and no KOFA complaint info.
  privateContext?: PrivateRejectionMessageContext;
  outsideProcurementAct?: boolean;
}

export const RejectSupplierModal = ({
  isOpen,
  onClose,
  supplierName,
  organizationNumber,
  foaPart,
  includeTenderGrounds = false,
  initialGroundKeys = [],
  attachments,
  onSubmit,
  isSubmitting,
  submitLabel,
  privateContext,
  outsideProcurementAct = false,
}: Props) => {
  const { t } = useTranslation();
  const complaintBodyApplies = !outsideProcurementAct && foaPart !== FoaPart.OutsideFoa;
  const titleSupplier = formatSupplierTitle(supplierName, organizationNumber);
  const [selectedKeys, setSelectedKeys] = useState<string[]>(initialGroundKeys);
  const [reason, setReason] = useState("");
  const [privateReasonContent, setPrivateReasonContent] = useState("");
  const [messageByGroundKey, setMessageByGroundKey] = useState<
    Record<string, string>
  >({});
  const [attachmentIds, setAttachmentIds] = useState<string[]>([]);
  const [areAttachmentsUploading, setAreAttachmentsUploading] = useState(false);
  const [openGroupIndexes, setOpenGroupIndexes] = useState<number[]>([]);
  const [groundSearch, setGroundSearch] = useState("");
  const expansionBeforeSearchRef = useRef<number[] | null>(null);
  const groundGroups = useMemo(
    () => getApplicableRejectionGroundGroups(foaPart, { includeTenderGrounds }),
    [foaPart, includeTenderGrounds],
  );

  const selectedGrounds = useMemo(
    () =>
      groundGroups
        .flatMap((group) => group.grounds)
        .filter((ground) => selectedKeys.includes(ground.key)),
    [groundGroups, selectedKeys],
  );
  const privateReasonMarkdown = useMemo(
    () => getGroundMessageMarkdown(privateReasonContent),
    [privateReasonContent],
  );
  const messageMarkdown = useMemo(
    () =>
      privateContext
        ? privateReasonMarkdown
          ? buildPrivateRejectionMessageMarkdown(privateContext, privateReasonMarkdown)
          : ""
        : buildSupplierRejectionMessageMarkdown(
            supplierName,
            selectedGrounds,
            messageByGroundKey,
            complaintBodyApplies,
          ),
    [privateContext, privateReasonMarkdown, supplierName, messageByGroundKey, selectedGrounds, complaintBodyApplies],
  );
  const uploadBlockedReason = areAttachmentsUploading
    ? t("Vent til vedleggene er ferdig lastet opp")
    : null;
  const submitBlockedReason = privateContext
    ? privateReasonMarkdown.length
      ? uploadBlockedReason
      : t("Skriv en begrunnelse")
    : selectedGrounds.length
      ? reason.trim().length
        ? uploadBlockedReason
        : t("Skriv en begrunnelse")
      : groundGroups.some((group) => group.kind === "reasons")
        ? t("Select at least one reason")
        : t("Velg minst én hjemmel");
  const canSubmit = submitBlockedReason === null && messageMarkdown.length > 0;

  const onAccordionChange = (nextIndex: number | number[]) => {
    setOpenGroupIndexes(Array.isArray(nextIndex) ? nextIndex : [nextIndex]);
  };

  const handleAttachmentIdsChange = useCallback((nextIds: string[]) => {
    setAttachmentIds(nextIds);
  }, []);

  const handleAttachmentsUploadingChange = useCallback(
    (isUploading: boolean) => {
      setAreAttachmentsUploading(isUploading);
    },
    [],
  );

  const updateGroundSearch = useCallback(
    (nextSearch: string) => {
      const wasSearching = groundSearch.trim().length > 0;
      const isSearching = nextSearch.trim().length > 0;

      if (!wasSearching && isSearching) {
        expansionBeforeSearchRef.current = openGroupIndexes;
        setOpenGroupIndexes(groundGroups.map((_, index) => index));
      }

      if (wasSearching && !isSearching) {
        setOpenGroupIndexes(expansionBeforeSearchRef.current ?? []);
        expansionBeforeSearchRef.current = null;
      }

      setGroundSearch(nextSearch);
    },
    [groundGroups, groundSearch, openGroupIndexes],
  );

  const toggleGround = (ground: RejectionGroundDto) => {
    setSelectedKeys((keys) =>
      keys.includes(ground.key)
        ? keys.filter((key) => key !== ground.key)
        : [...keys, ground.key],
    );
  };

  const updateGroundMessage = (
    ground: RejectionGroundDto,
    content: string,
  ) => {
    setMessageByGroundKey((messages) => ({
      ...messages,
      [ground.key]: content,
    }));
  };

  const submit = async () => {
    if (!canSubmit) return;
    await onSubmit({
      grounds: privateContext ? [] : selectedGrounds,
      reason: privateContext ? privateReasonMarkdown : reason.trim(),
      message: messageMarkdown,
      attachmentIds,
    });
  };

  return (
    <Modal
      isCentered
      isOpen={isOpen}
      onClose={onClose}
      size="6xl"
      scrollBehavior="inside"
    >
      <ModalOverlay />
      <ModalContent
        rounded="2xl"
        overflow="hidden"
        maxH={{ base: "calc(100vh - 2rem)", md: "calc(100vh - 4rem)" }}
        my={{ base: "4", md: "8" }}
        display="flex"
        flexDirection="column"
      >
        <ModalHeader
          px={{ base: "5", md: "8" }}
          py="6"
          borderBottom="1px solid"
          borderColor="red.100"
          bg="red.50"
          color="smText"
          flexShrink={0}
        >
          {t("Avvis {{supplier}}", { supplier: titleSupplier })}
        </ModalHeader>
        <ModalCloseButton top="5" right={{ base: "5", md: "8" }} size="lg" />
        <ModalBody px={{ base: "5", md: "8" }} py="6" flex="1" overflowY="auto">
          <SimpleGrid
            columns={{ base: 1, lg: privateContext ? 1 : 2 }}
            spacing="6"
            alignItems="start"
          >
            {!privateContext && (
            <Stack spacing="3">
              <Text fontWeight="semibold" color="smText" mb="2">
                {t("Velg lovhjemmel")}
              </Text>
              <InputGroup size="sm">
                <Input
                  aria-label={
                    t("Søk i hjemler eller beskrivelser") ?? undefined
                  }
                  placeholder={t("Søk i hjemler eller beskrivelser") ?? ""}
                  value={groundSearch}
                  onChange={(e) => updateGroundSearch(e.target.value)}
                  pr={groundSearch ? "9" : undefined}
                />
                {groundSearch ? (
                  <InputRightElement>
                    <IconButton
                      aria-label={t("Tøm søk") ?? undefined}
                      icon={<FiX />}
                      size="xs"
                      variant="ghost"
                      onClick={() => updateGroundSearch("")}
                    />
                  </InputRightElement>
                ) : null}
              </InputGroup>
              {groundGroups.length ? (
                <Accordion
                  allowMultiple
                  reduceMotion
                  index={openGroupIndexes}
                  onChange={onAccordionChange}
                >
                  {groundGroups.map((group, index) => (
                    <LegalBasisGroupPanel
                      key={`${group.subject}-${group.kind}-${group.description}`}
                      group={group}
                      namesSubject={groundGroups.some((candidate) => candidate.subject === "tender")}
                      isOpen={openGroupIndexes.includes(index)}
                      search={groundSearch}
                      selectedKeys={selectedKeys}
                      onToggleGround={toggleGround}
                      messageByGroundKey={messageByGroundKey}
                      onUpdateGroundMessage={updateGroundMessage}
                    />
                  ))}
                </Accordion>
              ) : (
                <Text fontSize="sm" color="smMuted">
                  {t(
                    "Det finnes ingen avvisningshjemler for denne anskaffelsens FOA-del.",
                  )}
                </Text>
              )}
            </Stack>
            )}

            <Stack spacing="5">
              {privateContext ? (
                <FormControl id="supplier-rejection-reason">
                  <FormLabel>{t("Begrunnelse")}</FormLabel>
                  <TipTap
                    content={privateReasonContent}
                    debounceWait={0}
                    maxHeight="44"
                    onUpdate={setPrivateReasonContent}
                    toolbar={{
                      headings: false,
                      colors: false,
                      link: false,
                      table: false,
                      image: false,
                      template: false,
                      autofill: false,
                      underline: false,
                    }}
                  />
                  <FormHelperText>
                    {t("Begrunnelsen settes inn i meldingen til leverandøren under.")}
                  </FormHelperText>
                </FormControl>
              ) : (
                <FormControl id="supplier-rejection-reason">
                  <FormLabel>{t("Begrunnelse")}</FormLabel>
                  <Textarea
                    id="supplier-rejection-reason"
                    aria-label={t("Begrunnelse") ?? undefined}
                    rows={4}
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder={
                      t(
                        "Beskriv faktum og vurderingen som gjør at leverandøren skal avvises.",
                      ) ?? ""
                    }
                  />
                </FormControl>
              )}

              {privateContext ? (
                <PrivateMessagePreview messageMarkdown={messageMarkdown} />
              ) : (
                <SupplierMessagePreview
                  supplierName={supplierName}
                  grounds={selectedGrounds}
                  messageByGroundKey={messageByGroundKey}
                  complaintBodyApplies={complaintBodyApplies}
                />
              )}

              <RejectionAttachmentsField
                source={attachments}
                onAttachmentIdsChange={handleAttachmentIdsChange}
                onUploadingChange={handleAttachmentsUploadingChange}
              />
            </Stack>
          </SimpleGrid>
        </ModalBody>
        <ModalFooter
          px={{ base: "5", md: "8" }}
          py="4"
          borderTop="1px solid"
          borderColor="smBorder"
          bg="smBackground"
          boxShadow="0 -8px 24px rgba(15, 23, 42, 0.05)"
          flexShrink={0}
        >
          <HStack spacing="3" align="center" flexWrap="wrap" rowGap="2">
            <Button variant="ghost" onClick={onClose}>
              {t("Cancel")}
            </Button>
            <Button
              colorScheme="red"
              onClick={submit}
              isDisabled={!canSubmit}
              isLoading={isSubmitting}
            >
              {submitLabel ?? t("Avvis leverandør")}
            </Button>
            {submitBlockedReason ? (
              <Text fontSize="sm" color="smMuted">
                {submitBlockedReason}
              </Text>
            ) : null}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

const LegalBasisGroupPanel = ({
  group,
  namesSubject,
  isOpen,
  search,
  selectedKeys,
  onToggleGround,
  messageByGroundKey,
  onUpdateGroundMessage,
}: {
  group: RejectionGroundGroup;
  namesSubject: boolean;
  isOpen: boolean;
  search: string;
  selectedKeys: string[];
  onToggleGround: (ground: RejectionGroundDto) => void;
  messageByGroundKey: Record<string, string>;
  onUpdateGroundMessage: (ground: RejectionGroundDto, content: string) => void;
}) => {
  const { t } = useTranslation();
  const normalizedSearch = search.trim().toLowerCase();
  const groupTitle = group.kind === "reasons"
    ? namesSubject
      ? {
          supplier: t("Reasons for rejection for the supplier"),
          tender: t("Reasons for rejection for the tender"),
        }[group.subject]
      : t("Reasons for rejection")
    : namesSubject
    ? {
        supplier: {
          mandatory: t("Mandatory legal bases for the supplier"),
          optional: t("Optional legal bases for the supplier"),
        },
        tender: {
          mandatory: t("Mandatory legal bases for the tender"),
          optional: t("Optional legal bases for the tender"),
        },
      }[group.subject][group.kind]
    : { mandatory: t("Pliktige avvisningshjemler"), optional: t("Valgfrie avvisningshjemler") }[group.kind];
  const selectedGrounds = group.grounds.filter((ground) =>
    selectedKeys.includes(ground.key),
  );
  const visibleGrounds = normalizedSearch
    ? group.grounds.filter((ground) =>
        [ground.label, ground.foaReference]
          .join(" ")
          .toLowerCase()
          .includes(normalizedSearch),
      )
    : group.grounds;

  return (
    <AccordionItem
      border="1px solid"
      borderColor="smBorder"
      rounded="md"
      mb="2"
      overflow="hidden"
    >
      <AccordionButton px="3" py="3" _expanded={{ bg: "gray.50" }}>
        <Box flex="1" textAlign="left" minW="0">
          <HStack spacing="3" justify="space-between" align="flex-start">
            <Box minW="0">
              <Text fontWeight="semibold" color="smText">
                {groupTitle}
              </Text>
              <Text fontSize="sm" color="smMuted" mt="1">
                {group.description}
              </Text>
            </Box>
            <Badge
              variant="subtle"
              colorScheme={selectedGrounds.length ? "teal" : "gray"}
              whiteSpace="nowrap"
            >
              {t("{{count}} valgt", { count: selectedGrounds.length })}
            </Badge>
          </HStack>
        </Box>
        <AccordionIcon ml="2" />
      </AccordionButton>
      <AccordionPanel px="3" pb="3" pt="0">
        {isOpen ? (
          <Stack spacing="2" pt="3">
            {visibleGrounds.length ? (
              visibleGrounds.map((ground) => {
                const isSelected = selectedKeys.includes(ground.key);
                const cardProps = {
                  role: "checkbox",
                  "aria-checked": isSelected,
                  "aria-label": `${ground.label} ${ground.foaReference}`.trim(),
                  tabIndex: 0,
                  border: "1px solid",
                  borderColor: isSelected ? "smPrimary" : "smBorder",
                  bg: "smBackground",
                  rounded: "md",
                  px: "3",
                  py: "2.5",
                  cursor: "pointer",
                  _hover: {
                    borderColor: isSelected ? "smPrimary" : "gray.300",
                  },
                  onClick: () => onToggleGround(ground),
                  onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
                    if (event.key !== " " && event.key !== "Enter") return;
                    event.preventDefault();
                    onToggleGround(ground);
                  },
                } as const;
                if (isSelected) {
                  return (
                    <Box key={ground.key} {...cardProps}>
                      <GroundSummary ground={ground} isSelected />
                      <GroundMessageEditor
                        ground={ground}
                        message={messageByGroundKey[ground.key] ?? ""}
                        onUpdateMessage={(content) =>
                          onUpdateGroundMessage(ground, content)
                        }
                      />
                    </Box>
                  );
                }

                return (
                  <Box key={ground.key} {...cardProps}>
                    <GroundSummary ground={ground} isSelected={false} />
                  </Box>
                );
              })
            ) : (
              <Text fontSize="sm" color="smMuted">
                {t("Ingen treff")}
              </Text>
            )}
          </Stack>
        ) : null}
      </AccordionPanel>
      {!isOpen && selectedGrounds.length ? (
        <Box
          borderTop="1px solid"
          borderColor="smBorder"
          bg="gray.50"
          px="3"
          py="3"
        >
          <Stack spacing="2">
            {selectedGrounds.map((ground) => (
              <SelectedGroundCard
                key={ground.key}
                ground={ground}
                onRemove={() => onToggleGround(ground)}
                message={messageByGroundKey[ground.key] ?? ""}
                onUpdateMessage={(content) =>
                  onUpdateGroundMessage(ground, content)
                }
              />
            ))}
          </Stack>
        </Box>
      ) : null}
    </AccordionItem>
  );
};

const GroundSummary = ({
  ground,
  isSelected,
}: {
  ground: RejectionGroundDto;
  isSelected: boolean;
}) => {
  const { t } = useTranslation();

  return (
    <HStack spacing="3" align="flex-start">
      <Box
        aria-hidden="true"
        alignItems="center"
        bg={isSelected ? "smPrimary" : "smBackground"}
        border="2px solid"
        borderColor={isSelected ? "smPrimary" : "gray.300"}
        boxSize="5"
        color="white"
        display="flex"
        flexShrink={0}
        justifyContent="center"
        mt="0.5"
        rounded="sm"
      >
        {isSelected ? <Icon as={FiCheck} boxSize="3.5" /> : null}
      </Box>
      <Stack spacing="1" flex="1" minW="0">
        <Text as="span" fontSize="sm" fontWeight="semibold" color="smText">
          {ground.label}
        </Text>
        {citesRegulation(ground) && (
          <HStack spacing="2" align="center" flexWrap="wrap">
            <Text as="span" fontSize="sm" color="smPrimary">
              {ground.foaReference}
            </Text>
            <Badge
              colorScheme={ground.mandatory ? "red" : "orange"}
              variant="subtle"
            >
              {ground.mandatory ? t("SKAL") : t("KAN")}
            </Badge>
          </HStack>
        )}
      </Stack>
    </HStack>
  );
};

const GroundMessageEditor = ({
  ground,
  message,
  onUpdateMessage,
}: {
  ground: RejectionGroundDto;
  message: string;
  onUpdateMessage: (content: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      aria-label={
        t("Melding for {{reference}}", {
          reference: citesRegulation(ground) ? ground.foaReference : ground.label,
        }) ?? undefined
      }
      mt="4"
      w="full"
      cursor="auto"
      onClick={(event) => event.stopPropagation()}
      onKeyDown={(event) => event.stopPropagation()}
    >
      <TipTap
        content={message}
        debounceWait={0}
        maxHeight="44"
        onUpdate={onUpdateMessage}
        toolbar={{
          headings: false,
          colors: false,
          link: false,
          table: false,
          image: false,
          template: false,
          autofill: false,
          underline: false,
        }}
      />
    </Box>
  );
};

const SelectedGroundCard = ({
  ground,
  onRemove,
  message,
  onUpdateMessage,
}: {
  ground: RejectionGroundDto;
  onRemove: () => void;
  message: string;
  onUpdateMessage: (content: string) => void;
}) => {
  const { t } = useTranslation();

  return (
    <Box
      border="1px solid"
      borderColor="smPrimarySoft"
      bg="smBackground"
      rounded="md"
      px="3"
      py="2"
    >
      <HStack spacing="3" align="flex-start" justify="space-between">
        <Box flex="1" minW="0">
          <GroundSummary ground={ground} isSelected />
        </Box>
        <IconButton
          aria-label={
            t("Fjern {{label}}", { label: ground.label }) ?? undefined
          }
          icon={<FiX />}
          size="xs"
          variant="ghost"
          onClick={onRemove}
        />
      </HStack>
      <GroundMessageEditor
        ground={ground}
        message={message}
        onUpdateMessage={onUpdateMessage}
      />
    </Box>
  );
};

const SupplierMessagePreview = ({
  supplierName,
  grounds,
  messageByGroundKey,
  complaintBodyApplies,
}: {
  supplierName: string;
  grounds: RejectionGroundDto[];
  messageByGroundKey: Record<string, string>;
  complaintBodyApplies: boolean;
}) => {
  const { t } = useTranslation();
  const previewGrounds = useMemo(
    () =>
      grounds.map((ground) => ({
        ground,
        message: getGroundMessageMarkdown(messageByGroundKey[ground.key] ?? ""),
      })),
    [grounds, messageByGroundKey],
  );

  return (
    <FormControl id="supplier-rejection-message">
      <FormLabel>{t("Melding til leverandør")}</FormLabel>
      <Box
        aria-label={t("Forhåndsvisning av melding") ?? undefined}
        border="1px solid"
        borderColor="smBorder"
        rounded="md"
        bg="smBackground"
        px="3"
        py="3"
        maxH="72"
        overflowY="auto"
      >
        {grounds.length ? (
          <Stack spacing="3">
            <Text fontSize="sm" color="smText">
              {supplierRejectionLead(supplierName, grounds)}
            </Text>
            {previewGrounds.map(({ ground, message }) => (
              <Box key={ground.key}>
                <Text fontSize="sm" fontWeight="semibold" color="smText">
                  {citesRegulation(ground) ? ground.foaReference : ground.label}
                </Text>
                {citesRegulation(ground) && (
                  <Text fontSize="sm" color="smMuted">
                    {ground.label}
                  </Text>
                )}
                {message ? (
                  <Text mt="2" fontSize="sm" color="smText" whiteSpace="pre-wrap">
                    {message}
                  </Text>
                ) : (
                  <Text mt="2" fontSize="sm" color="smMuted" fontStyle="italic">
                    {t("Ingen tekst skrevet for denne hjemmelen ennå.")}
                  </Text>
                )}
              </Box>
            ))}
            {complaintBodyApplies && (
              <Box borderTop="1px solid" borderColor="smBorder" pt="3">
                <Text fontSize="sm" fontWeight="semibold" color="smText">
                  {t("Klageadgang")}
                </Text>
                <Text fontSize="sm" color="smText" whiteSpace="pre-wrap">
                  {REJECTION_COMPLAINT_INFO}
                </Text>
              </Box>
            )}
          </Stack>
        ) : (
          <Text mt="3" fontSize="sm" color="smMuted">
            {t("Velg minst én lovhjemmel for å skrive melding.")}
          </Text>
        )}
      </Box>
      <FormHelperText>
        {t(
          "Forhåndsvisningen oppdateres fra teksten du skriver i de valgte lovhjemlene.",
        )}
      </FormHelperText>
    </FormControl>
  );
};

const PrivateMessagePreview = ({ messageMarkdown }: { messageMarkdown: string }) => {
  const { t } = useTranslation();
  const content = useMemo(
    () => (messageMarkdown ? getTiptapJsonFromMarkdown(messageMarkdown) : null),
    [messageMarkdown],
  );

  return (
    <FormControl id="supplier-rejection-message">
      <FormLabel>{t("Melding til leverandør")}</FormLabel>
      <Box
        aria-label={t("Forhåndsvisning av melding") ?? undefined}
        border="1px solid"
        borderColor="smBorder"
        rounded="md"
        bg="smBackground"
        px="3"
        py="3"
        maxH="72"
        overflowY="auto"
      >
        {content ? (
          <TipTap content={content} editable={false} hasBorder={false} padding="0" maxHeight="full" />
        ) : (
          <Text fontSize="sm" color="smMuted">
            {t("Skriv en begrunnelse for å se meldingen.")}
          </Text>
        )}
      </Box>
      <FormHelperText>
        {t("Forhåndsvisningen oppdateres fra begrunnelsen du skriver.")}
      </FormHelperText>
    </FormControl>
  );
};

const RejectionAttachmentsField = ({
  source,
  onAttachmentIdsChange,
  onUploadingChange,
}: {
  source: RejectionAttachmentsSource;
  onAttachmentIdsChange: (attachmentIds: string[]) => void;
  onUploadingChange: (isUploading: boolean) => void;
}) => {
  const { t } = useTranslation();
  const [modalUploadIds, setModalUploadIds] = useState<string[]>([]);
  const [removedUploadIds, setRemovedUploadIds] = useState<string[]>([]);
  const { entityType, entityId, eventId, documents, refetch } = source;
  const { uppy, allFiles, setDocuments, removeUpload } = useUppyUploader({
    entityType,
    entityId,
    eventId,
    reloadEntity: refetch,
  });

  useEffect(() => {
    const handleFileAdded = (file: { id: string }) => {
      setModalUploadIds((ids) =>
        ids.includes(file.id) ? ids : [...ids, file.id],
      );
    };

    uppy.on("file-added", handleFileAdded);
    return () => {
      uppy.off("file-added", handleFileAdded);
    };
  }, [uppy]);

  useEffect(() => {
    setDocuments(
      documents.filter(
        (document) =>
          modalUploadIds.includes(document.fromUploadId) &&
          !removedUploadIds.includes(document.fromUploadId),
      ),
    );
  }, [documents, modalUploadIds, removedUploadIds, setDocuments]);

  useEffect(() => {
    onAttachmentIdsChange(
      allFiles.filter(isCompletedUpload).map((file) => file.documentId),
    );
    onUploadingChange(allFiles.some(isIncompleteFileUpload));
  }, [allFiles, onAttachmentIdsChange, onUploadingChange]);

  const handleRemoveUpload = useCallback(
    async (file: FileUpload) => {
      await removeUpload(file);
      setRemovedUploadIds((ids) =>
        ids.includes(file.uploadId) ? ids : [...ids, file.uploadId],
      );
      setModalUploadIds((ids) => ids.filter((id) => id !== file.uploadId));
    },
    [removeUpload],
  );

  return (
    <Box>
      <DropTarget
        targetId={`supplier-rejection-attachments-${entityId}`}
        uppy={uppy}
        label={t("Vedlegg (valgfritt)") ?? undefined}
        helperText={t("Last opp dokumenter som skal følge avvisningen") ?? ""}
        paddingBottom="0"
      />
      {allFiles.length ? (
        <Box mt="3">
          <Table variant="simple" size="sm">
            <Tbody>
              {allFiles.map((file) => (
                <FileRow
                  key={file.uploadId}
                  file={file}
                  name={file.name}
                  remove={handleRemoveUpload}
                />
              ))}
            </Tbody>
          </Table>
        </Box>
      ) : null}
    </Box>
  );
};

type CompletedFileUpload = Extract<
  FileUpload,
  { status: UploadCompletedStatus.UploadCompleted }
>;

const isCompletedUpload = (file: FileUpload): file is CompletedFileUpload =>
  file.status === UploadCompletedStatus.UploadCompleted;

const formatSupplierTitle = (
  supplierName: string,
  organizationNumber?: string | null,
) => {
  if (!organizationNumber?.trim()) return supplierName;
  return `${supplierName} ${formatOrganizationNumber(organizationNumber)}`;
};

const formatOrganizationNumber = (orgNumber: string) =>
  orgNumber.replace(/^(\d{3})(\d{3})(\d{3})$/, "$1 $2 $3");
